import React, { useState } from "react";
import {
  Paper,
  Text,
  Title,
  SimpleGrid,
  Group,
  ThemeIcon,
  RingProgress,
  Center,
  Loader,
  Select,
  Stack,
  Badge,
  Progress,
  Flex,
  Alert,
  Button,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import {
  Stethoscope,
  Book,
  Pencil,
  Trophy,
  Filter,
  AlertCircle,
  X,
} from "tabler-icons-react";
import { useGetFeatureUsageStatsQuery } from "../dasboard/api/features/queriesApi";

const FEATURE_CONFIG = {
  DIAGNOSTIC: {
    label: "Diagnostic",
    color: "violet",
    icon: Stethoscope,
  },
  LESSON_QUIZ: {
    label: "Vérifier la leçon",
    color: "blue",
    icon: Book,
  },
  EXERCISE_CORRECTION: {
    label: "Corriger l'exercice",
    color: "teal",
    icon: Pencil,
  },
};

function FeatureUsageStats() {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [groupBy, setGroupBy] = useState(null);

  const queryParams = {};
  if (startDate) queryParams.start_date = startDate.toISOString().split("T")[0];
  if (endDate) queryParams.end_date = endDate.toISOString().split("T")[0];
  if (groupBy) queryParams.group_by = groupBy;

  const { data, isLoading, isFetching, error, refetch } =
    useGetFeatureUsageStatsQuery(queryParams, { pollingInterval: 1800000 });

  const stats = data?.data;
  const ranking = stats?.ranking || [];
  const totalUsages = stats?.total_usages || 0;

  const topFeature = ranking.length > 0 ? ranking[0] : null;

  const clearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setGroupBy(null);
  };

  const hasFilters = startDate || endDate || groupBy;

  const ringData = ranking.map((item) => ({
    value: item.percentage,
    color: FEATURE_CONFIG[item.feature]?.color || "gray",
    tooltip: `${FEATURE_CONFIG[item.feature]?.label || item.label}: ${item.count}`,
  }));

  return (
    <Stack spacing="lg" mb="xl">
      <Paper p="md" radius="md" shadow="sm" withBorder>
        <Flex gap="sm" align="center" wrap="wrap" justify="space-between">
          <Title order={4}>Usage des fonctionnalités</Title>

          <Flex gap="sm" align="center" wrap="wrap">
            <DatePickerInput
              placeholder="Date début"
              value={startDate}
              onChange={setStartDate}
              style={{ minWidth: 140 }}
              maxDate={new Date()}
              clearable
              locale="fr"
              size="xs"
            />
            <DatePickerInput
              placeholder="Date fin"
              value={endDate}
              onChange={setEndDate}
              style={{ minWidth: 140 }}
              minDate={startDate}
              maxDate={new Date()}
              clearable
              locale="fr"
              size="xs"
            />
            <Select
              placeholder="Regrouper"
              data={[
                { value: "day", label: "Par jour" },
                { value: "week", label: "Par semaine" },
                { value: "month", label: "Par mois" },
              ]}
              value={groupBy}
              onChange={setGroupBy}
              clearable
              size="xs"
              style={{ minWidth: 120 }}
              icon={<Filter size={14} />}
            />
            {hasFilters && (
              <Button
                variant="subtle"
                color="gray"
                size="xs"
                onClick={clearFilters}
                leftIcon={<X size={14} />}
              >
                Reset
              </Button>
            )}
            <Button
              size="xs"
              variant="light"
              onClick={() => refetch()}
              loading={isFetching}
            >
              Actualiser
            </Button>
          </Flex>
        </Flex>
      </Paper>

      {error && (
        <Alert
          icon={<AlertCircle size="1rem" />}
          title="Erreur"
          color="red"
          variant="light"
        >
          Impossible de charger les statistiques d'utilisation.
          <Button variant="light" size="xs" onClick={() => refetch()} mt="xs">
            Réessayer
          </Button>
        </Alert>
      )}

      {isLoading ? (
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      ) : (
        <>
          <SimpleGrid
            cols={4}
            breakpoints={[
              { maxWidth: "lg", cols: 4 },
              { maxWidth: "md", cols: 2 },
              { maxWidth: "sm", cols: 1 },
            ]}
          >
            <Paper
              withBorder
              p="md"
              radius="md"
              shadow="sm"
              style={{ opacity: isFetching ? 0.6 : 1 }}
            >
              <Group position="apart">
                <div>
                  <Text c="dimmed" tt="uppercase" fw={700} fz="xs">
                    Total utilisations
                  </Text>
                  <Text fw={700} fz="xl" mt={10}>
                    {totalUsages.toLocaleString()}
                  </Text>
                </div>
                <ThemeIcon color="indigo" variant="light" size={38} radius="md">
                  <Trophy size="1.4rem" />
                </ThemeIcon>
              </Group>
            </Paper>

            {ranking?.map((item) => {
              const config = FEATURE_CONFIG[item.feature] || {
                label: item.label,
                color: "gray",
                icon: Book,
              };
              const Icon = config.icon;
              return (
                <Paper
                  key={item.feature}
                  withBorder
                  p="md"
                  radius="md"
                  shadow="sm"
                  style={{ opacity: isFetching ? 0.6 : 1 }}
                >
                  <Group position="apart">
                    <div>
                      <Text c="dimmed" tt="uppercase" fw={700} fz="xs">
                        {config.label}
                      </Text>
                      <Text fw={700} fz="xl" mt={10}>
                        {item.count.toLocaleString()}
                      </Text>
                      <Badge
                        size="sm"
                        color={config.color}
                        variant="light"
                        mt={4}
                      >
                        {item.percentage}%
                      </Badge>
                    </div>
                    <ThemeIcon
                      color={config.color}
                      variant="light"
                      size={38}
                      radius="md"
                    >
                      <Icon size="1.4rem" />
                    </ThemeIcon>
                  </Group>
                </Paper>
              );
            })}
          </SimpleGrid>

          {ranking?.length > 0 && (
            <SimpleGrid
              cols={2}
              breakpoints={[{ maxWidth: "md", cols: 1 }]}
            >
              <Paper
                withBorder
                p="lg"
                radius="md"
                shadow="sm"
                style={{ opacity: isFetching ? 0.6 : 1 }}
              >
                <Text fw={600} fz="md" mb="md">
                  Répartition
                </Text>
                <Center>
                  <RingProgress
                    size={200}
                    thickness={24}
                    roundCaps
                    sections={ringData}
                    label={
                      <Center>
                        <Stack spacing={0} align="center">
                          <Text fw={700} fz="xl">
                            {totalUsages}
                          </Text>
                          <Text c="dimmed" fz="xs">
                            total
                          </Text>
                        </Stack>
                      </Center>
                    }
                  />
                </Center>
                <Stack spacing="xs" mt="md">
                  {ranking?.map((item) => {
                    const config = FEATURE_CONFIG[item.feature] || {
                      label: item.label,
                      color: "gray",
                    };
                    return (
                      <Group key={item.feature} position="apart">
                        <Group spacing="xs">
                          <div
                            style={{
                              width: 12,
                              height: 12,
                              borderRadius: "50%",
                              backgroundColor: `var(--mantine-color-${config.color}-6)`,
                              background:
                                config.color === "violet"
                                  ? "#7950f2"
                                  : config.color === "blue"
                                    ? "#228be6"
                                    : config.color === "teal"
                                      ? "#12b886"
                                      : "#868e96",
                            }}
                          />
                          <Text fz="sm">{config.label}</Text>
                        </Group>
                        <Text fz="sm" fw={600}>
                          {item.count} ({item.percentage}%)
                        </Text>
                      </Group>
                    );
                  })}
                </Stack>
              </Paper>

              <Paper
                withBorder
                p="lg"
                radius="md"
                shadow="sm"
                style={{ opacity: isFetching ? 0.6 : 1 }}
              >
                <Text fw={600} fz="md" mb="md">
                  Classement
                </Text>
                <Stack spacing="md">
                  {ranking.map((item, index) => {
                    const config = FEATURE_CONFIG[item.feature] || {
                      label: item.label,
                      color: "gray",
                      icon: Book,
                    };
                    const Icon = config.icon;
                    const medals = ["🥇", "🥈", "🥉"];
                    return (
                      <div key={item.feature}>
                        <Group position="apart" mb={4}>
                          <Group spacing="xs">
                            <Text fz="lg">{medals[index] || `#${index + 1}`}</Text>
                            <Icon size="1rem" />
                            <Text fz="sm" fw={500}>
                              {config.label}
                            </Text>
                          </Group>
                          <Text fz="sm" fw={600} color={config.color}>
                            {item.count.toLocaleString()} utilisations
                          </Text>
                        </Group>
                        <Progress
                          value={item.percentage}
                          color={config.color}
                          size="lg"
                          radius="md"
                          animate={index === 0}
                        />
                      </div>
                    );
                  })}
                </Stack>

                {topFeature && (
                  <Paper
                    mt="lg"
                    p="md"
                    radius="md"
                    bg={`${FEATURE_CONFIG[topFeature.feature]?.color || "gray"}.0`}
                    withBorder
                  >
                    <Group spacing="xs">
                      <Text fz="lg">🏆</Text>
                      <div>
                        <Text fz="sm" fw={600}>
                          Fonctionnalité la plus utilisée
                        </Text>
                        <Text fz="md" fw={700} color={FEATURE_CONFIG[topFeature.feature]?.color || "dark"}>
                          {FEATURE_CONFIG[topFeature.feature]?.label || topFeature.label}
                        </Text>
                      </div>
                    </Group>
                  </Paper>
                )}
              </Paper>
            </SimpleGrid>
          )}

          {ranking?.length === 0 && !isLoading && (
            <Paper withBorder p="xl" radius="md" ta="center">
              <Text c="dimmed" fz="lg">
                Aucune donnée d'utilisation disponible
              </Text>
              <Text c="dimmed" fz="sm" mt="xs">
                Les statistiques apparaîtront lorsque les parents utiliseront
                les fonctionnalités (Diagnostic, Leçon, Correction).
              </Text>
            </Paper>
          )}
        </>
      )}
    </Stack>
  );
}

export default FeatureUsageStats;
